"use client"

import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"

const BRAND = {
  darkBlue: "#174A84",
  midBlue: "#2A70B0",
  lightBlue: "#3A6FCC",
  gold: "#F4B333",
  red: "#DF463B",
  grey: "#9B9EA0",
  silver: "#E1E3E4",
  white: "#FFFFFF",
  nearBlack: "#1A1A2E",
} as const

const LABEL = "#4B5563"

type QuoteFormProps = {
  /** "card" = self-contained white card (for embedding inside a hero/column).
   *  "bar" = full-width peeking bar (homepage SkinnyFormBar style). */
  variant?: "card" | "bar"
  className?: string
}

const FIELD_LABEL =
  "text-xs font-normal mb-1 block normal-case tracking-normal"

/**
 * "Request a Medical Flight Quote" form — all fields revealed at once.
 * variant "card" embeds inside a hero/column; "bar" is the full-width peeking bar.
 */
export function QuoteForm({ variant = "card", className }: QuoteFormProps) {
  const [submitted, setSubmitted] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
  }

  const cardStyle =
    variant === "bar"
      ? {
          maxWidth: 1000,
          backgroundColor: BRAND.white,
          borderRadius: 12,
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
        }
      : {
          backgroundColor: BRAND.white,
          borderRadius: 12,
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
        }

  const wrapperClass =
    variant === "bar"
      ? "relative z-20 px-5 sm:px-6 form-peek-section"
      : "relative"

  const wrapperStyle =
    variant === "bar"
      ? { paddingBottom: 32, paddingTop: 16, backgroundColor: BRAND.white }
      : undefined

  if (submitted) {
    return (
      <section id="quote-form" className={`${wrapperClass} ${className ?? ""}`} style={wrapperStyle}>
        <div
          className="mx-auto flex items-center justify-center px-5 py-6 sm:px-8 sm:py-7"
          style={cardStyle}
        >
          <div className="flex items-center gap-4">
            <div
              className="w-10 h-10 flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: BRAND.darkBlue, borderRadius: 8 }}
            >
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p
                className="font-heading font-semibold text-sm md:text-base"
                style={{ color: BRAND.darkBlue }}
              >
                Request Received — A personal medical flight advisor will contact you within minutes at (800) 426-6557.
              </p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="quote-form" className={`${wrapperClass} ${className ?? ""}`} style={wrapperStyle}>
      <form
        onSubmit={handleSubmit}
        className={variant === "bar" ? "mx-auto px-5 py-6 sm:px-7" : "px-5 py-6 sm:px-7"}
        style={cardStyle}
      >
        {/* Header */}
        <div className="pb-4 mb-5 border-b" style={{ borderColor: BRAND.silver }}>
          <h3
            className="font-heading font-extrabold text-xl md:text-2xl leading-tight"
            style={{ color: BRAND.darkBlue }}
          >
            Request a Medical Flight Quote
          </h3>
          <p className="text-xs font-light mt-1" style={{ color: "#6B7280" }}>
            24/7 — We&apos;re Ready to Respond
          </p>
        </div>

        {/* All fields — revealed at once */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-5">
          <div>
            <Label htmlFor="patient-name" className={FIELD_LABEL} style={{ color: LABEL }}>
              Patient Name
            </Label>
            <Input id="patient-name" required placeholder="Full name" className="text-sm h-10" />
          </div>
          <div>
            <Label htmlFor="your-name" className={FIELD_LABEL} style={{ color: LABEL }}>
              Your Name
            </Label>
            <Input id="your-name" required placeholder="Your full name" className="text-sm h-10" />
          </div>
          <div>
            <Label htmlFor="condition" className={FIELD_LABEL} style={{ color: LABEL }}>
              Patient Condition / Needs
            </Label>
            <Input id="condition" required placeholder="Brief description" className="text-sm h-10" />
          </div>
          <div>
            <Label htmlFor="from" className={FIELD_LABEL} style={{ color: LABEL }}>
              From
            </Label>
            <Input id="from" required placeholder="City, State or Facility" className="text-sm h-10" />
          </div>
          <div>
            <Label htmlFor="to" className={FIELD_LABEL} style={{ color: LABEL }}>
              To
            </Label>
            <Input id="to" required placeholder="City, State or Facility" className="text-sm h-10" />
          </div>
          <div>
            <Label htmlFor="phone" className={FIELD_LABEL} style={{ color: LABEL }}>
              Phone Number
            </Label>
            <Input id="phone" type="tel" required placeholder="(800) 000-0000" className="text-sm h-10" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="email" className={FIELD_LABEL} style={{ color: LABEL }}>
              Email Address
            </Label>
            <Input id="email" type="email" required placeholder="you@example.com" className="text-sm h-10" />
          </div>
        </div>

        {/* Submit */}
        <Button
          type="submit"
          className="w-full font-heading font-bold text-sm tracking-[0.05em] h-11 transition-all duration-300 ease-out hover:scale-[1.01] hover:shadow-lg active:scale-[0.99] cta-lightblue"
          style={{
            backgroundColor: BRAND.lightBlue,
            color: BRAND.white,
            borderRadius: 6,
            letterSpacing: "0.05em",
          }}
        >
          GET MEDICAL FLIGHT COSTS NOW
        </Button>

        {/* HIPAA footer */}
        <p className="text-center text-xs mt-5" style={{ color: BRAND.grey }}>
          Your information is secure and confidential. HIPAA Compliant.
        </p>
      </form>
    </section>
  )
}
